import { Link } from 'react-router'
import {
  Star,
  Gift,
  Zap,
  LogOut,
  Sparkles,
  MessageSquare,
  Image,
  Sun,
  Moon,
  Monitor,
} from 'lucide-react'
import { useSidebarStore } from '@/stores/sidebarStore'
import { useIsAbove768 } from '@/hooks/useMediaQuery'
import { type RouterOutput } from '@/config/trpc'
import { useUserContext } from '@/context/userLogin'
import { useTheme } from '@/context/themeContext'
import { Card } from '../ui/shadcn/card'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/shadcn/tooltip'
import { ArrowIcon } from '../ui/icons/icons-new'
import { formatDate, cn } from '@/lib/utils'
import { plans, TRIAL_DURATION_DAYS } from '@backend/lib/config/pricing'
import { Button } from '../ui/shadcn/button'
import { useState } from 'react'
import { ClaimTrialModal } from '../claim-trial-modal'

export type UsageLimit = RouterOutput['usageLimit']['get']
interface Props {
  plan: string
  isCanceled: boolean
  currentPeriodEnd?: string | null
  remainingUsage?: UsageLimit
  isUserSubscribedLoading: boolean
  isRemainingGenerationsLoading: boolean
  isTrial?: boolean
  trialExpiresAt?: string | null
  hadTrial?: boolean
}

/** -1 from the API means unlimited */
function UsageBar({
  icon,
  label,
  used,
  max,
}: {
  icon: React.ReactNode
  label: string
  used: number
  max: number
}) {
  if (max === -1) return null // unlimited — don't render a bar
  const ratio = max > 0 ? used / max : 0
  return (
    <div className="flex flex-col space-y-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {icon}
          <p className="text-muted-foreground">{label}</p>
        </div>
        <span className="text-muted-foreground">
          {used}/{max}
        </span>
      </div>
      <div className="bg-muted h-1.5 w-full overflow-hidden rounded-full">
        <div
          className={`h-full rounded-full ${ratio < 0.8 ? 'bg-emerald-500' : 'bg-amber-500'}`}
          style={{ width: `${ratio * 100}%` }}
        />
      </div>
    </div>
  )
}

export function SidebarFooter({
  plan,
  isCanceled,
  currentPeriodEnd,
  remainingUsage,
  isUserSubscribedLoading,
  isRemainingGenerationsLoading,
  isTrial,
  trialExpiresAt,
  hadTrial,
}: Props) {
  const { logout } = useUserContext()
  const { closeSidebar } = useSidebarStore()
  const isAbove768 = useIsAbove768()

  const [claimTrialOpen, setClaimTrialOpen] = useState(false)

  const planIcon = plan === 'free' ? <Gift className="h-4 w-4" /> : <Star className="h-4 w-4" />
  const refreshDate = remainingUsage?.refreshDate ? formatDate(remainingUsage.refreshDate) : ''

  // Every tier except Enterprise has at least one hard monthly cap worth
  // surfacing (unlimited counters hide their own bars). The trial keeps its
  // countdown card instead.
  const showUsageBars = !isTrial && plan !== 'enterprise'
  const isLoading = isUserSubscribedLoading || (showUsageBars && isRemainingGenerationsLoading)

  const planLabel = isTrial
    ? 'Trial'
    : (plans[plan]?.name ?? plan.charAt(0).toUpperCase() + plan.slice(1))

  const daysRemaining = (() => {
    if (!isTrial || !trialExpiresAt) return null
    const now = new Date()
    const expires = new Date(trialExpiresAt)
    return Math.max(0, Math.ceil((expires.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)))
  })()

  return (
    <div className="space-y-1.5">
      <div>
        {isLoading ? (
          <div className="bg-muted animate-pulse rounded-md px-2 py-2 text-xs">
            <div className="flex flex-col space-y-2">
              <div className="bg-muted h-5 w-24 animate-pulse rounded"></div>
            </div>
          </div>
        ) : showUsageBars && remainingUsage ? (
          <Link to="/account/subscription" onClick={isAbove768 ? undefined : () => closeSidebar()}>
            <Card className="border-border hover:bg-hover rounded-md bg-transparent p-2 text-xs transition-colors md:p-2">
              <div className="flex flex-col space-y-2">
                <div className="flex items-center gap-1.5 text-sm">
                  {planIcon}
                  {planLabel} plan
                </div>
                <div className="flex flex-col space-y-2">
                  <UsageBar
                    icon={<Zap className="h-3.5 w-3.5 text-yellow-500" />}
                    label="Posts"
                    used={remainingUsage.post.max - remainingUsage.post.remaining}
                    max={remainingUsage.post.max}
                  />
                  <UsageBar
                    icon={<Image className="h-3.5 w-3.5 text-blue-500" />}
                    label="Images"
                    used={remainingUsage.image.max - remainingUsage.image.remaining}
                    max={remainingUsage.image.max}
                  />
                  <UsageBar
                    icon={<MessageSquare className="h-3.5 w-3.5 text-purple-500" />}
                    label="Chats"
                    used={remainingUsage.chat.max - remainingUsage.chat.remaining}
                    max={remainingUsage.chat.max}
                  />

                  {refreshDate && (
                    <div className="text-muted-foreground mt-1 text-right text-xs">
                      {remainingUsage.needsNewPeriod
                        ? 'Generate to start new period'
                        : `Resets on ${refreshDate}`}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </Link>
        ) : isTrial ? (
          <Link to="/account/subscription" onClick={isAbove768 ? undefined : () => closeSidebar()}>
            <div className="cursor-pointer rounded-md border border-blue-500/60 bg-transparent px-2 py-2 text-xs transition-colors hover:border-stone-400">
              <div className="flex w-full flex-col gap-2">
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-1.5 text-sm">
                    {planIcon}
                    Pro Trial
                  </div>
                  <div className="ml-auto">
                    <ArrowIcon width={20} height={20} color="var(--foreground)" />
                  </div>
                </div>
                {daysRemaining !== null && (
                  <p className="text-muted-foreground">
                    {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining
                  </p>
                )}
                {/* The trial is Pro limits except images, so that one capped
                    counter is the only bar worth showing here. */}
                {remainingUsage && (
                  <UsageBar
                    icon={<Image className="h-3.5 w-3.5 text-blue-500" />}
                    label="Images"
                    used={remainingUsage.image.max - remainingUsage.image.remaining}
                    max={remainingUsage.image.max}
                  />
                )}
              </div>
            </div>
          </Link>
        ) : (
          <div className="cursor-pointer rounded-md border border-amber-500/60 bg-transparent px-2 py-2 text-xs transition-colors hover:border-stone-400">
            <Link
              to="/account/subscription"
              className="flex w-full flex-col gap-2"
              onClick={isAbove768 ? undefined : () => closeSidebar()}>
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center gap-1.5 text-sm">
                  {planIcon}
                  {planLabel} plan
                </div>
                <div className="ml-auto">
                  <ArrowIcon width={20} height={20} color="var(--foreground)" />
                </div>
              </div>
              {isCanceled && currentPeriodEnd && (
                <p className="text-muted-foreground">
                  Subscription ends at {formatDate(currentPeriodEnd)}
                </p>
              )}
            </Link>
          </div>
        )}
      </div>

      {plan === 'free' && !hadTrial && (
        <>
          <Button
            variant="outline"
            size="sm"
            className="w-full gap-2"
            onClick={() => setClaimTrialOpen(true)}>
            <Gift className="h-4 w-4 text-amber-500" />
            Try Pro free for {TRIAL_DURATION_DAYS} days
          </Button>
          <ClaimTrialModal open={claimTrialOpen} onClose={() => setClaimTrialOpen(false)} />
        </>
      )}
      {isTrial && (
        <Link to="/pricing" onClick={isAbove768 ? undefined : () => closeSidebar()}>
          <Button variant="outline" size="sm" className="w-full gap-2">
            <Sparkles className="h-4 w-4 text-amber-500" />
            Choose your plan
          </Button>
        </Link>
      )}

      <SidebarFooterControls onSignOut={() => logout()} />
    </div>
  )
}

function SidebarFooterControls({ onSignOut }: { onSignOut: () => void }) {
  const { theme, setTheme } = useTheme()

  const themeOptions = [
    { value: 'system' as const, label: 'System theme', Icon: Monitor },
    { value: 'light' as const, label: 'Light theme', Icon: Sun },
    { value: 'dark' as const, label: 'Dark theme', Icon: Moon },
  ]

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-0.5">
          {themeOptions.map(({ value, label, Icon }) => (
            <Tooltip key={value}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={label}
                  aria-pressed={theme === value}
                  onClick={() => setTheme(value)}
                  className={cn(
                    'flex h-8 w-8 cursor-pointer items-center justify-center rounded-md transition-colors',
                    theme === value
                      ? 'bg-hover text-foreground'
                      : 'text-muted-foreground hover:bg-hover hover:text-foreground',
                  )}>
                  <Icon className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">{label}</TooltipContent>
            </Tooltip>
          ))}
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label="Sign out"
              onClick={onSignOut}
              className="text-muted-foreground hover:bg-hover hover:text-foreground flex h-8 w-8 cursor-pointer items-center justify-center rounded-md transition-colors">
              <LogOut className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">Sign out</TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  )
}
